import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Wordmark } from "@/components/Logo";
import { MaskLines } from "@/components/landing/Reveal";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">{label}</span>
    <input {...props} data-testid={testid} className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-lg outline-none focus:border-[#F59E0B] transition-colors placeholder:text-[#F5F1E8]/30" />
  </label>
);

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const target = (u) => {
    const from = location.state?.from;
    if (u?.role === "admin") return from?.startsWith("/admin") || from?.startsWith("/resumen") ? from : "/admin";
    return from && !from.startsWith("/admin") ? from : "/panel";
  };

  if (user) return <Navigate to={target(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form);
      navigate(target(u), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen grid lg:grid-cols-2" data-testid={`${mode}-page`}>
      <section className="relative overflow-hidden min-h-[260px] lg:min-h-screen">
        <img src="/img/p3.jpg" alt="Inspector de seguridad en planta" className="absolute inset-0 w-full h-full object-cover grayscale-[30%]" />
        <div className="absolute inset-0 bg-[#0B0F17]/65" />
        <div className="relative h-full p-6 sm:p-10 flex flex-col justify-between gap-10">
          <Wordmark />
          <h1 className="font-display-x text-4xl sm:text-6xl font-black uppercase leading-[0.9] tracking-tight">
            <MaskLines lines={isLogin ? ["Bienvenido", "de vuelta", <span key="x" className="text-[#F59E0B]">a planta.</span>] : ["Tu primera", "auditoría", <span key="x" className="text-[#F59E0B]">empieza aquí.</span>]} />
          </h1>
        </div>
      </section>
      <section className="flex items-center px-6 sm:px-12 lg:px-20 py-14">
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} onSubmit={submit} className="w-full max-w-md space-y-7" data-testid="auth-form">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">{isLogin ? "Acceso" : "Registro"}</div>
            <h2 className="font-display text-3xl font-extrabold mt-3">{isLogin ? "Inicia sesión" : "Crea tu cuenta"}</h2>
          </div>
          {!isLogin && <Field label="Nombre completo" testid="register-name-input" value={form.name} onChange={set("name")} required minLength={2} placeholder="Ana López" />}
          {!isLogin && <Field label="Empresa (opcional)" testid="register-company-input" value={form.company} onChange={set("company")} placeholder="Manufacturas del Norte" />}
          <Field label="Correo" testid="auth-email-input" type="email" value={form.email} onChange={set("email")} required placeholder="tu@empresa.mx" />
          <Field label="Contraseña" testid="auth-password-input" type="password" value={form.password} onChange={set("password")} required minLength={6} placeholder="Mínimo 6 caracteres" />
          {error && <p className="text-sm text-rose-400" data-testid="auth-error">{error}</p>}
          <button disabled={loading} data-testid="auth-submit-button" className="group w-full inline-flex items-center justify-between bg-[#F59E0B] text-[#0B0F17] pl-7 pr-2 py-2 rounded-full font-semibold hover:bg-[#F5F1E8] transition-colors disabled:opacity-60">
            {isLogin ? "Entrar" : "Crear cuenta"}
            <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0B0F17] text-[#F59E0B]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />}
            </span>
          </button>
          <p className="text-sm text-[#F5F1E8]/70">
            {isLogin ? "¿Aún no tienes cuenta? " : "¿Ya tienes cuenta? "}
            <Link to={isLogin ? "/registro" : "/login"} state={location.state} data-testid="auth-switch-link" className="text-[#F59E0B] underline underline-offset-4">
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </Link>
          </p>
        </motion.form>
      </section>
    </main>
  );
}
